import { Injectable } from '@angular/core';
import type { OktaUserInfo } from '@brightside/core/auth';
import type { EmployeeSearchResponse } from './employee-search.service';

const DATABASE_NAME = 'brighthub-employee-search-cache';
const DATABASE_VERSION = 1;
const STORE_NAME = 'queries';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface EmployeeSearchCacheEntry {
  key: string;
  expiresAt: number;
  response: EmployeeSearchResponse;
}

@Injectable({ providedIn: 'root' })
export class EmployeeSearchCacheService {
  private readonly _memory = new Map<string, EmployeeSearchCacheEntry>();
  private _database: Promise<IDBDatabase> | null = null;

  async get(
    userId: string | null,
    query: string,
  ): Promise<EmployeeSearchResponse | null> {
    if (!userId) return null;
    const key = this._key(userId, query);
    const memory = this._memory.get(key);
    if (memory && memory.expiresAt > Date.now()) return memory.response;
    this._memory.delete(key);
    if (!globalThis.indexedDB) return null;
    try {
      const database = await this._open();
      const transaction = database.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const entry = await this._request<EmployeeSearchCacheEntry | undefined>(
        store.get(this._key(userId, query)),
      );
      if (!entry) return null;
      if (entry.expiresAt <= Date.now()) {
        const deleteTransaction = database.transaction(STORE_NAME, 'readwrite');
        deleteTransaction.objectStore(STORE_NAME).delete(entry.key);
        await this._transaction(deleteTransaction);
        return null;
      }
      this._remember(entry);
      return entry.response;
    } catch {
      return null;
    }
  }

  async set(
    userId: string | null,
    query: string,
    response: EmployeeSearchResponse,
  ): Promise<void> {
    if (!userId) return;
    const entry: EmployeeSearchCacheEntry = {
      key: this._key(userId, query),
      expiresAt: Date.now() + CACHE_TTL_MS,
      response,
    };
    this._remember(entry);
    if (!globalThis.indexedDB) return;
    try {
      const database = await this._open();
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      transaction.objectStore(STORE_NAME).put(entry);
      await this._transaction(transaction);
    } catch {
      // IndexedDB is an optimization; the API response remains authoritative.
    }
  }

  /** Search discovered employees across unexpired queries for this signed-in user. */
  async findUsers(
    userId: string | null,
    query: string,
  ): Promise<OktaUserInfo[]> {
    if (!userId || !query.trim()) return [];
    const prefix = `${userId.trim().toLowerCase()}::`;
    let entries = [...this._memory.values()];
    if (globalThis.indexedDB) {
      try {
        const database = await this._open();
        const transaction = database.transaction(STORE_NAME, 'readonly');
        const persisted = await this._request<EmployeeSearchCacheEntry[]>(
          transaction
            .objectStore(STORE_NAME)
            .getAll(IDBKeyRange.bound(prefix, `${prefix}\uffff`)),
        );
        entries = [...persisted, ...entries];
      } catch {
        // Memory results remain available when browser storage is unavailable.
      }
    }
    const tokens = query.trim().toLowerCase().split(/\s+/);
    const users = new Map<string, OktaUserInfo>();
    for (const entry of entries.sort((a, b) => a.expiresAt - b.expiresAt)) {
      if (!entry.key.startsWith(prefix) || entry.expiresAt <= Date.now())
        continue;
      for (const user of entry.response.payload.users) {
        if (user.id) users.set(user.id, user);
      }
    }
    return [...users.values()]
      .filter((user) => {
        const text =
          `${user.firstName} ${user.lastName} ${user.email}`.toLowerCase();
        return tokens.every((token) => text.includes(token));
      })
      .slice(0, 10);
  }

  private _remember(entry: EmployeeSearchCacheEntry): void {
    this._memory.delete(entry.key);
    this._memory.set(entry.key, entry);
    for (const [key, cached] of this._memory) {
      if (cached.expiresAt <= Date.now()) this._memory.delete(key);
    }
    while (this._memory.size > 100) {
      this._memory.delete(this._memory.keys().next().value!);
    }
  }

  private _key(userId: string, query: string): string {
    return `${userId.trim().toLowerCase()}::${query.trim().toLowerCase()}`;
  }

  private _open(): Promise<IDBDatabase> {
    if (this._database) return this._database;

    const database = new Promise<IDBDatabase>((resolve, reject) => {
      const request = globalThis.indexedDB.open(
        DATABASE_NAME,
        DATABASE_VERSION,
      );
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: 'key' });
        }
      };
      request.onsuccess = () => {
        request.result.onversionchange = () => {
          request.result.close();
          this._database = null;
        };
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('Employee cache is blocked.'));
    }).catch((error: unknown) => {
      this._database = null;
      throw error;
    });
    this._database = database;
    return database;
  }

  private _request<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private _transaction(transaction: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }
}
